import moment from 'moment-timezone';
import { colors } from 'ui/theme/colors';
import {
  displayDate,
  getDaysAgo,
  getMonthsAgo,
  getNewDate,
  getWeeksAgo,
  setDate,
} from 'utils/date';
import { displayTime, setTime } from 'utils/time';

/** Helper function that checks if all inputs are valid */
export const checkIfAllInputsAreValid = (inputs) =>
  Object.keys(inputs).reduce((validity, inputKey) => validity && inputs[inputKey], true);

/** Helper function to set time zone without changing the date & time displayed */
export const setTimeZoneWithSameTime = (currentMoment /*: moment */, timeZone /*: string */) => {
  const time = displayTime(currentMoment);
  const date = displayDate(currentMoment);

  const momentWithTimeZone = moment.tz(currentMoment, timeZone);
  const momentWithSameTime = setTime(momentWithTimeZone, time); // maintain time
  return setDate(momentWithSameTime, date); // maintain date
};

/** Helper function to fetch a list of the regional names of all time zones */
export const getAllTimeZones = () => moment.tz.names(true);

/** Helper function to get the display text in the time zone picker dropdown */
export const getTimeZoneMenuDisplay = (timezone /*: string */) => {
  const zone = moment.tz(timezone);
  const offset = zone.utcOffset() / 60;
  return `${timezone} (${zone.format('z')}), UTC ${offset >= 0 ? '+' : ''}${offset}`;
};

/** Helper function to select all the text of an input */
export const selectInputText = (inputRef) => inputRef?.current?.select();

export const START_DATE = 'startDate';
export const END_DATE = 'endDate';
const DAY = 'day';
export const TODAY = 'TODAY';
export const PAST_3_DAYS = 'PAST_3_DAYS';
export const PAST_7_DAYS = 'PAST_7_DAYS';
export const PAST_30_DAYS = 'PAST_30_DAYS';
export const CUSTOM_RANGE = 'CUSTOM_RANGE';
export const ALL_TIME = 'ALL_TIME';

/** Placeholder texts for date picker inputs */
export const DATE_PLACEHOLDER = 'mm/dd/yyyy';
export const NULL_DATE_PLACEHOLDER = '--';
export const TIME_PLACEHOLDER = 'HH:mm';
export const RELATIVE_DATE_PLACEHOLDER = 'Set a date and time';

/** Helper function that sets new date while preserving the time of original date */
export const setDateWithMoment = (currentDate /*: moment */, newDate /*: moment */) =>
  newDate ? setDate(currentDate, displayDate(newDate)) : currentDate;

/**
 * Definition of a few time presets to show on calendar.
 */
export const RELATIVE_DATE_PRESETS = {
  TODAY: {
    menuLabel: 'Today',
    filterLabel: 'today',
    format: (timeZone) => ({
      startDate: getNewDate({ timeZone }).startOf(DAY),
      endDate: getNewDate({ timeZone }).endOf(DAY),
    }),
  },
  PAST_3_DAYS: {
    menuLabel: 'Past 3 days',
    filterLabel: 'in the past 3 days',
    format: (timeZone) => ({
      startDate: getDaysAgo(getNewDate({ timeZone }), 3).startOf(DAY),
      endDate: getNewDate({ timeZone }).endOf(DAY),
    }),
  },
  PAST_7_DAYS: {
    menuLabel: 'Past 7 days',
    filterLabel: 'in the past 7 days',
    format: (timeZone) => ({
      startDate: getWeeksAgo(getNewDate({ timeZone }), 1).startOf(DAY),
      endDate: getNewDate({ timeZone }).endOf(DAY),
    }),
  },
  PAST_30_DAYS: {
    menuLabel: 'Past 30 days',
    filterLabel: 'in the past 30 days',
    format: (timeZone) => ({
      startDate: getMonthsAgo(getNewDate({ timeZone }), 1).startOf(DAY),
      endDate: getNewDate({ timeZone }).endOf(DAY),
    }),
  },
  CUSTOM_RANGE: {
    menuLabel: 'Custom range',
  },
};

export const RELATIVE_DATE_PRESETS_WITH_LABELS = {
  TODAY: {
    ...RELATIVE_DATE_PRESETS.TODAY,
    menuLabel: 'timePresets.menu.today',
    filterLabel: 'timePresets.filter.today',
  },
  PAST_3_DAYS: {
    ...RELATIVE_DATE_PRESETS.PAST_3_DAYS,
    menuLabel: 'timePresets.menu.3Days',
    filterLabel: 'timePresets.filter.3Days',
  },
  PAST_7_DAYS: {
    ...RELATIVE_DATE_PRESETS.PAST_7_DAYS,
    menuLabel: 'timePresets.menu.7Days',
    filterLabel: 'timePresets.filter.7Days',
  },
  PAST_30_DAYS: {
    ...RELATIVE_DATE_PRESETS.PAST_30_DAYS,
    menuLabel: 'timePresets.menu.30Days',
    filterLabel: 'timePresets.filter.30Days',
  },
  CUSTOM_RANGE: {
    menuLabel: 'timePresets.menu.custom',
    filterLabel: 'timePresets.filter.custom',
  },
};

/**
 * Definition of a few time presets with 'All time' option to show on calendar.
 */
export const RELATIVE_DATE_PRESETS_WITH_ALL_TIME = {
  TODAY: RELATIVE_DATE_PRESETS.TODAY,
  PAST_3_DAYS: RELATIVE_DATE_PRESETS.PAST_3_DAYS,
  PAST_7_DAYS: RELATIVE_DATE_PRESETS.PAST_7_DAYS,
  PAST_30_DAYS: RELATIVE_DATE_PRESETS.PAST_30_DAYS,
  ALL_TIME: {
    menuLabel: 'All time',
    filterLabel: 'for all time',
    format: () => ({
      startDate: null,
      endDate: null,
    }),
  },
  CUSTOM_RANGE: RELATIVE_DATE_PRESETS.CUSTOM_RANGE,
};

/** Helper function to determine color of text and icon for inputs */
export const colorDeterminator = (isValid, isFocused, isDisabled = false) => {
  if (isDisabled) return colors.textDisabled;
  if (isFocused) return colors.purple100;
  return isValid ? colors.textPrimary : colors.negative100;
};
